// =============================================================================
// Dashboard Works — preview modal, works view, PDF download, publish dialog,
// credits history, and preview event listeners
// Extracted from make_album/dashboard.html
// =============================================================================

import type {
  Album,
  AlbumDetail,
  PageData,
  PublishState,
  PreviewState,
  ExportJobStatus,
  CategoryKeyword,
} from './dashboard-types';

import {
  loadAlbumFromDB,
  loadUserAlbums,
  loadTemplateRegistry,
  getTemplateMeta,
  getPageRenderSize,
  renderSavedPageIntoHost,
  invalidateAlbumsCache,
  deleteAlbumFromDB,
  _loadTemplateData,
  _cssTextCache,
  cleanupRenderHost,
} from './dashboard-data';

import {
  showToast,
  showConfirm,
  showWarning,
  formatTime,
  escapeHtml,
} from './dashboard-ui';

// ========================
// Global declarations
// ========================

declare function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
  opts?: { immediate?: boolean }
): void;

declare const QRCode: any;

import { renderView } from './dashboard-auth';
import { openUnifiedOrderModal } from './dashboard-orders';

// ========================
// Preview Modal State
// ========================

const _previewState: PreviewState = {
  albumId: null,
  albumTitle: '',
  spreads: [],
  currentIndex: 0,
  albumPageBaseSize: 400,
};

// ========================
// Album Preview Modal
// ========================

export async function openPreviewModal(albumId: number | string): Promise<void> {
  albumId = parseInt(String(albumId));
  try {
    const data = await loadAlbumFromDB(albumId);
    const albums = await loadUserAlbums();
    const album = albums.find(function (a) { return a.id === albumId; });
    _previewState.albumId = albumId as number;
    _previewState.albumTitle = album ? album.title : '未命名相册';
    _previewState.spreads = data.spreads || [];
    _previewState.currentIndex = 0;
    _previewState.albumPageBaseSize = data.albumPageBaseSize || 400;

    document.getElementById('previewTitle')!.textContent = _previewState.albumTitle;
    document.getElementById('previewModalOverlay')!.classList.remove('hidden');
    await loadTemplateRegistry();
    renderPreviewSpread();
  } catch (e) {
    console.error('Failed to open preview:', e);
  }
}

export function closePreviewModal(): void {
  document.getElementById('previewModalOverlay')!.classList.add('hidden');
  const host = document.getElementById('previewPageContainer') as HTMLElement | null;
  cleanupRenderHost(host);
  _previewState.albumId = null;
}

export async function renderPreviewSpread(): Promise<void> {
  const host = document.getElementById('previewPageContainer') as HTMLElement | null;
  cleanupRenderHost(host);
  const spreads = _previewState.spreads;
  if (!spreads || spreads.length === 0) return;
  const idx = _previewState.currentIndex;
  const spread = spreads[idx];
  if (!spread) return;
  const totalSpreads = spreads.length;

  const isCover = spread._isCover;
  const isBackCover = spread._isBackCover;

  // 总页数 = 内页跨页数 × 2（不含封面封底）
  let innerSpreadCount = 0;
  for (let si = 0; si < totalSpreads; si++) {
    if (!spreads[si]._isCover && !spreads[si]._isBackCover) innerSpreadCount++;
  }
  const totalInnerPages = innerSpreadCount * 2;

  let indicatorText: string;
  if (isCover) {
    indicatorText = '封面';
  } else if (isBackCover) {
    indicatorText = '封底';
  } else {
    // Count inner spreads before this one
    let innerIdx = 0;
    for (let si = 0; si < idx; si++) {
      if (!spreads[si]._isCover && !spreads[si]._isBackCover) innerIdx++;
    }
    const startPage = innerIdx * 2 + 1;
    indicatorText = '第' + startPage + '-' + (startPage + 1) + ' / 共' + totalInnerPages + '页';
  }
  document.getElementById('previewPagination')!.textContent = indicatorText;
  (document.getElementById('previewPrev') as HTMLButtonElement).disabled = idx <= 0;
  (document.getElementById('previewNext') as HTMLButtonElement).disabled = idx >= totalSpreads - 1;

  const leftPage = spread.left;
  const rightPage = spread.right;
  const unifiedPageData = spread._spreadPageData || null;
  if (!leftPage && !rightPage && !unifiedPageData) return;

  let pageSize: { width: number; height: number };
  if (unifiedPageData) {
    pageSize = getPageRenderSize(unifiedPageData);
  } else {
    pageSize = leftPage ? getPageRenderSize(leftPage) : getPageRenderSize(rightPage);
  }
  const baseW = pageSize.width;
  const baseH = pageSize.height;

  // Create container for the spread
  const container = document.createElement('div');
  container.style.cssText = 'display:flex;align-items:center;justify-content:center;position:relative;';

  if (unifiedPageData) {
    // Unified spread (双页) template with _spreadPageData
    // Render full-width, then clip left/right halves like marketplace preview
    const fullW = baseW;
    const fullH = baseH;
    const halfW = Math.round(fullW / 2);
    container.style.width = fullW + 'px';
    container.style.height = fullH + 'px';

    const leftWrapper = document.createElement('div');
    leftWrapper.style.cssText = 'width:' + halfW + 'px;height:' + fullH + 'px;overflow:hidden;position:relative;';
    const leftInner = document.createElement('div');
    leftInner.style.cssText = 'position:absolute;left:0;width:' + fullW + 'px;height:' + fullH + 'px;';
    leftWrapper.appendChild(leftInner);
    container.appendChild(leftWrapper);

    const rightWrapper = document.createElement('div');
    rightWrapper.style.cssText = 'width:' + halfW + 'px;height:' + fullH + 'px;overflow:hidden;position:relative;';
    const rightInner = document.createElement('div');
    rightInner.style.cssText = 'position:absolute;left:-' + halfW + 'px;width:' + fullW + 'px;height:' + fullH + 'px;';
    rightWrapper.appendChild(rightInner);
    container.appendChild(rightWrapper);

    host!.appendChild(container);
    renderSavedPageIntoHost(leftInner, unifiedPageData, _previewState.albumId || '');
    renderSavedPageIntoHost(rightInner, unifiedPageData, _previewState.albumId || '');
  } else if (rightPage && leftPage) {
    // Check if this uses a unified spread (双页) template
    const leftMeta = leftPage && leftPage.templateId ? getTemplateMeta(leftPage.templateId) : null;
    const rightMeta = rightPage && rightPage.templateId ? getTemplateMeta(rightPage.templateId) : null;
    const isUnified = (leftMeta && leftMeta.category === '双页') ||
      (rightMeta && rightMeta.category === '双页') ||
      leftPage.templateCategory === '双页' ||
      rightPage.templateCategory === '双页' ||
      leftPage.isUnifiedSpread ||
      rightPage.isUnifiedSpread;

    if (isUnified) {
      // Spread template: clip each half, like marketplace preview
      const halfW = Math.round(baseW / 2);
      container.style.width = baseW + 'px';
      container.style.height = baseH + 'px';

      // Left half — clip to show left side only
      const leftWrapper = document.createElement('div');
      leftWrapper.style.cssText = 'width:' + halfW + 'px;height:' + baseH + 'px;overflow:hidden;position:relative;';
      const leftInner = document.createElement('div');
      leftInner.style.cssText = 'position:absolute;left:0;width:' + baseW + 'px;height:' + baseH + 'px;';
      leftWrapper.appendChild(leftInner);
      container.appendChild(leftWrapper);

      // Right half — offset to show right side
      const rightWrapper = document.createElement('div');
      rightWrapper.style.cssText = 'width:' + halfW + 'px;height:' + baseH + 'px;overflow:hidden;position:relative;';
      const rightInner = document.createElement('div');
      rightInner.style.cssText = 'position:absolute;left:-' + halfW + 'px;width:' + baseW + 'px;height:' + baseH + 'px;';
      rightWrapper.appendChild(rightInner);
      container.appendChild(rightWrapper);

      host!.appendChild(container);
      renderSavedPageIntoHost(leftInner, leftPage, _previewState.albumId || '');
      renderSavedPageIntoHost(rightInner, rightPage, _previewState.albumId || '');
    } else {
      // Regular double-page spread (two independent single-page templates)
      const leftSize = getPageRenderSize(leftPage);
      const rightSize = getPageRenderSize(rightPage);
      const leftW = leftSize.width;
      const leftH = leftSize.height;
      const rightW = rightSize.width;
      const rightH = rightSize.height;
      const spreadH = Math.max(leftH, rightH);
      container.style.width = (leftW + rightW) + 'px';
      container.style.height = spreadH + 'px';

      const leftHost = document.createElement('div');
      leftHost.style.cssText = 'width:' + leftW + 'px;height:' + leftH + 'px;overflow:hidden;position:relative;';
      container.appendChild(leftHost);

      const rightHost = document.createElement('div');
      rightHost.style.cssText = 'width:' + rightW + 'px;height:' + rightH + 'px;overflow:hidden;position:relative;';
      container.appendChild(rightHost);

      host!.appendChild(container);
      renderSavedPageIntoHost(leftHost, leftPage, _previewState.albumId || '');
      renderSavedPageIntoHost(rightHost, rightPage, _previewState.albumId || '');
    }
  } else if (leftPage) {
    // Single page (cover, back cover, or single-page spread)
    container.style.width = baseW + 'px';
    container.style.height = baseH + 'px';
    host!.appendChild(container);
    renderSavedPageIntoHost(container, leftPage, _previewState.albumId || '');
  } else if (rightPage) {
    container.style.width = baseW + 'px';
    container.style.height = baseH + 'px';
    host!.appendChild(container);
    renderSavedPageIntoHost(container, rightPage, _previewState.albumId || '');
  }

  // Scale to fit
  const scaleX = host!.clientWidth / parseFloat(container.style.width);
  const scaleY = host!.clientHeight / parseFloat(container.style.height);
  const scale = Math.min(scaleX, scaleY, 1);
  container.style.transform = 'scale(' + scale + ')';
  container.style.transformOrigin = 'center center';
}

export function goToSpread(index: number): void {
  const spreads = _previewState.spreads;
  if (index < 0 || index >= spreads.length) return;
  _previewState.currentIndex = index;
  renderPreviewSpread();
}

// ========================
// View: My Works
// ========================

function renderAlbumCards(albums: Album[], container: HTMLElement): void {
  let html = '';
  albums.forEach(function (album) {
    const isEditing = album.status === 'editing';
    let actionBtnHtml = '';
    if (isEditing) {
      actionBtnHtml = '<button class="btn btn-sm order-btn" data-id="' + album.id + '">下单</button>';
    } else {
      const pubState = albumPublishStates[album.id];
      if (pubState) {
        if (pubState.status === 'published') {
          actionBtnHtml = '<span class="publish-badge published">已上架</span>';
        } else if (pubState.status === 'rejected') {
          actionBtnHtml = '<span class="publish-badge rejected" data-reason="' + (pubState.review_reason || '未提供原因').replace(/"/g, '&quot;') + '">发布失败</span>';
        } else if (pubState.status === 'unpublished') {
          actionBtnHtml = '<span class="publish-badge unpublished">已下架</span>';
        } else if (pubState.status === 'removed') {
          actionBtnHtml = '<span class="publish-badge removed" data-pt-id="' + pubState.id + '" data-reason="' + (pubState.remove_reason || '') + '">已移除</span>';
        }
      } else {
        actionBtnHtml = '<button class="btn btn-sm publish-btn" data-id="' + album.id + '">发布<span class="btn-tooltip">发布到模板商城，<br>被其他用户使用后，您可获得积分</span></button>';
      }
    }

    const coverHtml =
      '<div class="album-live-preview" data-preview-id="' + album.id + '">' +
      (album.thumbnail
        ? '<img src="' + album.thumbnail + '" alt="封面">'
        : '<div style="width:100%;height:100%;background:linear-gradient(135deg,' +
          '#e0e5ec 0%, #a3b1c6 100%);display:flex;align-items:center;justify-content:center;' +
          'color:rgba(255,255,255,0.3);font-size:24px;">&#x1F4F7;</div>') +
      '</div>';
    const tmplName = album.templateAlbumId || '自由创作';
    html += '<div class="album-card" data-id="' + album.id + '">' +
      '<div class="card-cover">' + coverHtml + '</div>' +
      '<div class="card-info">' +
      '<div class="card-title" title="' + album.title + '">' + album.title + '</div>' +
      '<div class="card-meta">' + formatTime(album.lastModified) + '</div>' +
      '</div>' +
      '<div class="card-actions">' +
      '<button class="btn btn-sm edit-btn" data-id="' + album.id + '" data-tmpl="' + (album.templateAlbumId || '') + '"><svg style="height:13px;vertical-align:-1px;margin-right:3px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M799.306 313.723v20.48h154.167v618.844h-882.098v-618.844h233.189v-20.48h-253.669v659.804h923.058v-659.804z"/><path d="M339.333 880.513h45.173v45.173h-45.173v-45.173z"/><path d="M640.119 880.513h45.394v45.173h-45.394v-45.173z"/><path d="M489.837 880.513h45.173v45.173h-45.173v-45.173z"/><path d="M293.186 582.762c-1.417 1.876-3.299 5.628-5.659 11.258-2.355 5.159-32.893 66.118-70.724 180.115-3.299 15.479-5.188 28.142-5.659 37.99 31.885-39.688 115.452-199.588 126.433-211.38 32.426-34.816 203.776-308.566 241.349-334.59-17.093 47.567-232.454 395.172-237.642 403.149 162.181 35.549 232.452-55.351 241.883-63.324l-71.436-12.667 86.995-26.030c57.315-58.356 72.847-149.86 72.847-149.86-6.603 2.343-75.911 22.749-87.702 26.032l94.775-51.36c6.599-25.799 56.584-142.582 58.704-331.383-25.463 1.878-283.72 77.869-445.583 505.162-1.411 8.446-0.94 14.074 1.417 16.888z"/></svg>编辑</button>' +
      '<button class="btn btn-sm delete-btn" data-id="' + album.id + '" aria-label="删除"><svg style="height:13px;vertical-align:-1px;margin-right:3px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M829.952 271.36h-126.976v-51.2a76.288 76.288 0 0 0-75.776-76.288h-256a76.288 76.288 0 0 0-75.776 76.8v51.2H168.448a25.6 25.6 0 0 0 0 51.2h661.504a25.6 25.6 0 0 0 0-51.712z m-178.176 0h-307.2v-51.2a26.112 26.112 0 0 1 25.6-25.6h256a24.576 24.576 0 0 1 24.576 25.6zM448.512 677.376V398.336a25.6 25.6 0 0 0-51.2 0v279.04a25.6 25.6 0 0 0 51.2 0zM601.088 677.376V398.336a25.6 25.6 0 0 0-51.2 0v279.04a25.6 25.6 0 0 0 51.2 0z" fill="#d81e06"/><path d="M735.744 346.624a25.6 25.6 0 0 0-25.6 25.6v381.44a102.4 102.4 0 0 1-102.4 102.4H390.656a102.4 102.4 0 0 1-102.4-102.4V372.224a25.6 25.6 0 0 0-51.2 0v381.44a153.6 153.6 0 0 0 153.6 153.6h217.6a153.6 153.6 0 0 0 153.6-153.6V372.224a25.6 25.6 0 0 0-26.112-25.6z" fill="#d81e06"/></svg>删除</button>' +
      actionBtnHtml +
      '</div></div>';
  });
  container.innerHTML += html;
  renderLiveAlbumPreviews(albums, container);
}

async function renderLiveAlbumPreviews(albums: Album[], container: HTMLElement): Promise<void> {
  await loadTemplateRegistry();
  for (let i = 0; i < albums.length; i++) {
    const album = albums[i];
    const preview = container.querySelector('.album-live-preview[data-preview-id="' + album.id + '"]') as HTMLElement | null;
    if (!preview) continue;
    try {
      const data = await loadAlbumFromDB(album.id);
      const firstPage = data && data.spreads && data.spreads[0] && data.spreads[0].left;
      if (!firstPage || !firstPage.html) continue;
      const pageSize = getPageRenderSize(firstPage);
      const meta = getTemplateMeta(firstPage.templateId);
      const isSpread = (meta && meta.category === '双页') || firstPage.templateCategory === '双页' || firstPage.isUnifiedSpread;
      // Only halve width if the page itself is a wide spread (width > height)
      const isWidePage = pageSize.width > pageSize.height;
      const baseW = (isSpread && isWidePage) ? Math.round(pageSize.width / 2) : pageSize.width;
      const baseH = pageSize.height;
      cleanupRenderHost(preview);
      const scale = Math.min(preview.clientWidth / baseW, preview.clientHeight / baseH);
      const offsetX = (preview.clientWidth - baseW * scale) / 2;
      const offsetY = (preview.clientHeight - baseH * scale) / 2;
      preview.innerHTML = '<div class="album-preview-inner" data-render-host="dashboard" style="width:' + baseW + 'px;height:' + baseH + 'px;transform-origin:0 0;position:absolute;top:' + offsetY + 'px;left:' + offsetX + 'px;overflow:hidden;"></div>';
      const inner = preview.querySelector('.album-preview-inner') as HTMLElement;
      inner.style.transform = 'scale(' + scale + ')';
      await renderSavedPageIntoHost(inner, firstPage, data.albumId);
    } catch (err) {
      console.warn('Failed to render live album preview:', album.id, err);
    }
  }
}

function bindAlbumCardEvents(container: HTMLElement): void {
  container.querySelectorAll('.edit-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const tmpl = this.getAttribute('data-tmpl');
      const id = this.getAttribute('data-id');
      let url = 'editor.html?saved=' + id;
      if (tmpl) url += '&album=' + tmpl;
      window.open(url, '_blank');
    });
  });
  container.querySelectorAll('.order-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      openUnifiedOrderModal(this.getAttribute('data-id')!);
    });
  });
  container.querySelectorAll('.delete-btn').forEach(function (btn) {
    btn.addEventListener('click', async function () {
      const id = parseInt(this.getAttribute('data-id')!);
      const ok = await showConfirm('删除相册', '确定删除这个相册吗？对应订单和发布到模板商城的相册将同步删除。');
      if (ok) {
        try {
          const pubState = albumPublishStates[id];
          await deleteAlbumFromDB(id);
          if (pubState) {
            _loadTemplateData().then(function (m) {
              m.markTemplateDataChanged({ scope: 'community', templateIds: [String(pubState.id)], at: Date.now() });
            });
          }
          renderView('works');
        } catch (err: any) {
          showWarning(err.message || '删除相册失败');
        }
      }
    });
  });
  container.querySelectorAll('.card-title').forEach(function (titleEl) {
    titleEl.addEventListener('click', function (e) {
      const card = (this as HTMLElement).closest('.album-card')!;
      const albumId = card.getAttribute('data-id');
      const oldTitle = this.textContent || '';
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'card-title-input';
      input.value = oldTitle;
      this.replaceWith(input);
      input.focus();
      input.select();
      function commit(): void {
        const newTitle = input.value.trim() || oldTitle;
        const div = document.createElement('div');
        div.className = 'card-title';
        div.textContent = newTitle;
        div.title = newTitle;
        input.replaceWith(div);
        bindAlbumCardEvents(container);
        if (newTitle !== oldTitle) {
          fetch('/api/albums/' + albumId, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: newTitle })
          }).then(function () {
            invalidateAlbumsCache();
          }).catch(function () { });
        }
      }
      input.addEventListener('keydown', function (ev: KeyboardEvent) {
        if (ev.key === 'Enter') { ev.preventDefault(); commit(); }
        if (ev.key === 'Escape') { input.value = oldTitle; commit(); }
      });
      input.addEventListener('blur', function () { commit(); });
    });
  });
  container.querySelectorAll('.card-cover').forEach(function (cover) {
    (cover as HTMLElement).style.cursor = 'pointer';
    cover.addEventListener('click', function (e) {
      // Don't trigger if clicking on edit/delete buttons inside
      if ((e.target as HTMLElement).closest('button')) return;
      const card = (this as HTMLElement).closest('.album-card')!;
      const albumId = parseInt(card.getAttribute('data-id')!);
      openPreviewModal(albumId);
    });
  });

  container.querySelectorAll('.publish-btn').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      const albumId = parseInt(btn.getAttribute('data-id')!);
      loadUserAlbums().then(function (cachedAlbums) {
        const album = cachedAlbums ? cachedAlbums.find(function (a) { return a.id === albumId; }) : null;
        if (album) { trackEvent('community_template_publish_click', { album_id: album.id }); openPublishDialog(album); }
      });
    });
  });

  container.querySelectorAll('.publish-badge.removed').forEach(function (badge) {
    badge.addEventListener('click', function (e) {
      e.stopPropagation();
      const reason = badge.getAttribute('data-reason') || '未提供原因';
      alert('该模板已被平台移除。\n原因：' + reason);
    });
  });

  container.querySelectorAll('.publish-badge.rejected').forEach(function (badge) {
    badge.addEventListener('click', function (e) {
      e.stopPropagation();
      const reason = badge.getAttribute('data-reason') || '未提供原因';
      alert('发布失败，审核未通过。\n\n原因：' + reason);
    });
  });
}

let albumPublishStates: Record<number, PublishState> = {};

function loadPublishStates(completedAlbums: Album[]): Promise<void> {
  albumPublishStates = {};
  if (!completedAlbums.length) return Promise.resolve();
  return fetch('/api/templates/published', {
    credentials: 'include'
  }).then(function (r) { return r.json(); }).then(function (list) {
    list.forEach(function (pt: any) {
      if (!albumPublishStates[pt.album_id]) albumPublishStates[pt.album_id] = pt;
    });
  }).catch(function () { /* ignore errors */ });
}

export async function renderMyWorks(container: HTMLElement): Promise<void> {
  const albums = await loadUserAlbums();
  // Ensure backward compatibility for albums without status
  albums.forEach(function (a) { if (!a.status) a.status = 'editing'; });

  const newId = 'ua_' + Date.now();
  let html = '<div class="section-head"><h2>我的相册</h2>' +
    '<a href="editor.html?saved=' + newId + '" class="btn btn-accent" target="_blank">+ 新建相册</a></div>';

  if (!albums.length) {
    html += '<div class="empty-state">' +
      '<div class="empty-icon">&#x1F4C1;</div>' +
      '<p>还没有相册</p>' +
      '<p style="font-size:12px;margin-top:4px;">' +
      '<a href="marketplace.html" style="color:var(--accent);text-decoration:none;">去模板商城选一个吧</a></p>' +
      '</div>';
    container.innerHTML = html;
    return;
  }

  const completed = albums.filter(function (a) { return a.status === 'completed'; });
  await loadPublishStates(completed);

  // Sort: editing first (newest first), then completed (newest first)
  albums.sort(function (a, b) {
    if (a.status !== b.status) {
      return a.status === 'editing' ? -1 : 1;
    }
    return (b.lastModified || 0) - (a.lastModified || 0);
  });

  html += '<div class="album-grid" id="works-grid"></div>';
  container.innerHTML = html;

  renderAlbumCards(albums, document.getElementById('works-grid')!);
  bindAlbumCardEvents(container);
}

// ========================
// PDF Download
// ========================

let _albumCSSLoaded = false;
let _injectedStyles: HTMLStyleElement[] = [];

export function loadAlbumCSS(): Promise<void> {
  if (_albumCSSLoaded) return Promise.resolve();
  _injectedStyles = [];

  // Inject essential page rendering CSS (same as editor's page-box styles)
  const essentialCSS = '\
    .editable-img, .img-cover, .page-box .img-cover, .album-preview-inner .img-cover { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; max-width:none; max-height:none; display:block; }\
    .img-slot, .page-box .img-slot, .album-preview-inner .img-slot { position:relative; overflow:hidden; }\
    .page-box .editable-text, .album-preview-inner .editable-text { outline:none; cursor:default; }\
  ';
  const essentialEl = document.createElement('style');
  essentialEl.textContent = essentialCSS;
  document.head.appendChild(essentialEl);
  _injectedStyles.push(essentialEl);

  // Load all album CSS files needed for template rendering
  // Apply same :root -> .page-box replacement as editor for CSS variable scoping
  const cssFiles = [
    'templates/storytelling/style.css',
    'templates/minimalism/style.css',
    'templates/memphis/style.css',
    'templates/memphis-xiaomi/style.css',
    'templates/social/style.css',
    'templates/nature/style.css',
    'templates/shanjian/style.css',
    'templates/neusocial/style.css',
    'templates/qingri/style.css'
  ];
  return Promise.all(cssFiles.map(function (cssPath) {
    return fetch(cssPath).then(function (r) {
      if (!r.ok) throw new Error('CSS not found: ' + cssPath);
      return r.text();
    }).then(function (rawCSS) {
      // Preload raw CSS into renderer cache
      const albumId = cssPath.replace('templates/', '').replace('/style.css', '');
      if (!_cssTextCache[albumId]) _cssTextCache[albumId] = rawCSS;

      const css = rawCSS.replace(/:root\s*\{/g, '.page-box, .thumb-page-inner, .layout-preview-inner, .album-preview-inner {');
      const el = document.createElement('style');
      el.textContent = css;
      document.head.appendChild(el);
      _injectedStyles.push(el);
    }).catch(function () { /* skip missing CSS */ });
  })).then(function () { _albumCSSLoaded = true; });
}

function unloadAlbumCSS(): void {
  _injectedStyles.forEach(function (el) {
    if (el.parentNode) el.parentNode.removeChild(el);
  });
  _injectedStyles = [];
  _albumCSSLoaded = false;
}

const PDF_STAGE_LABELS: Record<string, string> = {
  preparing: '准备页面数据...',
  queueing: '等待渲染队列...',
  loading: '加载页面内容...',
  rendering: '渲染页面...',
  composing: '合成 PDF 文件...',
  done: '生成完成',
  failed: '导出失败'
};

let _pdfPollTimer: ReturnType<typeof setInterval> | null = null;

export async function downloadPDF(
  albumId: number | string,
  albumTitle: string,
  btn: HTMLButtonElement,
  orderId?: string | null,
  publishedTemplateId?: string | null
): Promise<void> {
  const origHTML = btn.innerHTML;
  btn.innerHTML = '生成中...';
  btn.disabled = true;

  trackEvent('pdf_download_click', { album_id: albumId, order_id: orderId || null, published_template_id: publishedTemplateId ? parseInt(publishedTemplateId, 10) : null }, { immediate: true });
  const pdfStartedAt = Date.now();

  const overlay = document.getElementById('pdfProgressOverlay')!;
  const progressBar = document.getElementById('pdfProgressBar') as HTMLElement;
  const stageEl = document.getElementById('pdfProgressStage')!;
  const detailEl = document.getElementById('pdfProgressDetail')!;
  const etaEl = document.getElementById('pdfProgressEta')!;
  const titleEl = document.getElementById('pdfProgressTitle')!;

  function showProgress(job: ExportJobStatus): void {
    titleEl.textContent = '正在生成 PDF...';
    overlay.classList.remove('hidden');
    stageEl.textContent = PDF_STAGE_LABELS[job.stage] || '';

    if (job.stage === 'rendering' && job.total && job.total > 0) {
      const pct = Math.round((job.current! / job.total) * 85) + 5;
      progressBar.style.width = pct + '%';
      detailEl.textContent = '第 ' + job.current + ' / ' + job.total + ' 页';
      if (job.current && job.current > 0) {
        const elapsed = Date.now() - pdfStartedAt;
        const remaining = Math.round((elapsed / job.current) * (job.total - job.current) / 1000);
        etaEl.textContent = '预计剩余 ' + remaining + ' 秒';
      }
    } else if (job.stage === 'composing') {
      progressBar.style.width = '90%';
      detailEl.textContent = '';
      etaEl.textContent = '';
    } else if (job.stage === 'loading') {
      progressBar.style.width = '5%';
      detailEl.textContent = '';
      etaEl.textContent = '';
    } else if (job.stage === 'queueing') {
      progressBar.style.width = '2%';
      detailEl.textContent = '';
      etaEl.textContent = '';
    } else if (job.stage === 'preparing') {
      progressBar.style.width = '1%';
      detailEl.textContent = '';
      etaEl.textContent = '';
    }
  }

  function hideProgress(): void {
    overlay.classList.add('hidden');
    if (_pdfPollTimer) { clearInterval(_pdfPollTimer); _pdfPollTimer = null; }
  }

  try {
    // Step 1: POST to start async export job
    const startResp = await fetch('/api/albums/' + albumId + '/export', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId: orderId || null })
    });
    if (!startResp.ok) {
      const errData = await startResp.json().catch(function () { return { message: '导出失败' }; });
      throw new Error(errData.message || '导出失败');
    }
    const startData = await startResp.json();
    const exportId = startData.exportId;
    const filename = startData.filename || (albumTitle || '相册').replace(/[^a-zA-Z0-9一-鿿]/g, '_') + '.pdf';

    // Step 2: Poll until done/failed
    await new Promise<void>(function (resolve, reject) {
      _pdfPollTimer = setInterval(function () {
        fetch('/api/albums/' + albumId + '/export/' + exportId + '/status', {
          credentials: 'include'
        }).then(function (pollResp) {
          if (!pollResp.ok) throw new Error('进度查询失败');
          return pollResp.json();
        }).then(function (job: ExportJobStatus) {
          showProgress(job);
          if (job.status === 'done') {
            clearInterval(_pdfPollTimer!); _pdfPollTimer = null;
            resolve();
          } else if (job.status === 'failed') {
            clearInterval(_pdfPollTimer!); _pdfPollTimer = null;
            reject(new Error(job.error || '导出失败'));
          }
        }).catch(function (pollErr) {
          clearInterval(_pdfPollTimer!); _pdfPollTimer = null;
          reject(pollErr);
        });
      }, 800);
    });

    // Step 3: Trigger browser native download via hidden iframe.
    // The download URL responds with Content-Disposition: attachment,
    // so the browser shows download progress in its native download shelf.
    const downloadUrl = '/api/albums/' + albumId + '/export/' + exportId + '/download?filename=' + encodeURIComponent(filename);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);
    // Clean up iframe after a reasonable time for the browser to start the download
    setTimeout(function () {
      document.body.removeChild(iframe);
    }, 5000);

    titleEl.textContent = '生成完成!';
    progressBar.style.width = '100%';
    stageEl.textContent = 'PDF 已开始下载，请查看浏览器下载列表';
    detailEl.textContent = '';
    etaEl.textContent = '';
    setTimeout(function () {
      hideProgress();
      btn.innerHTML = '<span style="color:#22c55e;font-size:16px;">&#10003;</span>';
      trackEvent('pdf_export_success', { album_id: albumId, duration_ms: Date.now() - pdfStartedAt }, { immediate: true });
    }, 1200);

  } catch (err: any) {
    hideProgress();
    btn.innerHTML = '<span style="color:#e53e3e;font-size:14px;">&#10007;</span>';
    showWarning('PDF 导出失败: ' + err.message);
    trackEvent('pdf_export_failed', { album_id: albumId, message: err.message || '导出失败' }, { immediate: true });
  }

  setTimeout(function () { btn.innerHTML = origHTML; btn.disabled = false; }, 1500);
}

// ========================
// Preview Modal Event Listeners
// ========================

export function initPreviewListeners(): void {
  document.getElementById('previewClose')!.addEventListener('click', closePreviewModal);
  document.getElementById('previewModalOverlay')!.addEventListener('click', function (e) {
    if (e.target === document.getElementById('previewModalOverlay')) closePreviewModal();
  });
  document.getElementById('previewPrev')!.addEventListener('click', function () {
    goToSpread(_previewState.currentIndex - 1);
  });
  document.getElementById('previewNext')!.addEventListener('click', function () {
    goToSpread(_previewState.currentIndex + 1);
  });
  document.getElementById('previewEditBtn')!.addEventListener('click', function () {
    const id = _previewState.albumId;
    loadUserAlbums().then(function (albumsCache) {
      let tmpl = '';
      if (albumsCache) {
        const found = albumsCache.find(function (a) { return a.id === id; });
        if (found) tmpl = found.templateAlbumId || '';
      }
      let url = 'editor.html?saved=' + id;
      if (tmpl) url += '&album=' + tmpl;
      window.open(url, '_blank');
      closePreviewModal();
    });
  });
  document.getElementById('previewOrderBtn')!.addEventListener('click', function () {
    const id = _previewState.albumId;
    closePreviewModal();
    if (id) openUnifiedOrderModal(id);
  });
  document.addEventListener('keydown', function (e: KeyboardEvent) {
    if (document.getElementById('previewModalOverlay')!.classList.contains('hidden')) return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); goToSpread(_previewState.currentIndex - 1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); goToSpread(_previewState.currentIndex + 1); }
    if (e.key === 'Escape') { e.preventDefault(); closePreviewModal(); }
  });
}

// ========================
// Publish Dialog
// ========================

const CATEGORY_KEYWORDS: CategoryKeyword[] = [
  { cat: '仪式感', words: ['婚', '嫁', '恋', '爱', '纪念', '节', '圣诞', '春节', '新年', '庆祝', '典礼', '周年', '生日'] },
  { cat: '旅行', words: ['旅行', '游', '海', '山', '城市', '风景', '路', '岛', '途', '川', '湖', '林', '漠'] },
  { cat: '成长', words: ['毕业', '学', '成长', '岁', '童', '亲', '子', '校园', '童年', '少年', '青春', '入学'] },
  { cat: '生活', words: ['美食', '吃', '食', '宠', '猫', '狗', '日常', '家', '生活', '周末', '午后', '闲'] },
  { cat: '人像', words: ['人像', '写真', '拍', '影', '约拍', '大片', '美照', '肖像', '自拍'] }
];

const ALL_CATEGORIES: string[] = ['仪式感', '旅行', '成长', '生活', '人像'];

function recommendCategory(title: string): string {
  title = (title || '').toLowerCase();
  for (let i = 0; i < CATEGORY_KEYWORDS.length; i++) {
    const entry = CATEGORY_KEYWORDS[i];
    for (let j = 0; j < entry.words.length; j++) {
      if (title.indexOf(entry.words[j]) !== -1) return entry.cat;
    }
  }
  return '生活';
}

export function openPublishDialog(album: Album): void {
  const recommendedCat = recommendCategory(album.title);

  let optionsHtml = '';
  ALL_CATEGORIES.forEach(function (cat) {
    const sel = cat === recommendedCat ? ' selected' : '';
    optionsHtml += '<option value="' + cat + '"' + sel + '>' + cat + '</option>';
  });

  const overlay = document.createElement('div');
  overlay.className = 'publish-overlay';
  overlay.id = 'publishOverlay';
  overlay.innerHTML =
    '<div class="publish-dialog">' +
    '<h3>发布模板到商城</h3>' +
    '<div class="field">' +
    '<label>模板名称</label>' +
    '<input type="text" id="pubName" value="' + escapeHtml(album.title) + '" maxlength="20">' +
    '</div>' +
    '<div class="field">' +
    '<label>分类</label>' +
    '<select id="pubCategory">' + optionsHtml + '</select>' +
    '</div>' +
    '<div class="field">' +
    '<label>描述（选填）</label>' +
    '<textarea id="pubDesc" maxlength="200" placeholder="可补充说明，比如适合什么场景..."></textarea>' +
    '</div>' +
    '<div class="notice">' +
    '📄 感谢分享！发布后模板进入社区模板库，若被他人使用，您将获得积分，平台保留对模板的管理和分发权。' +
    '<a href="terms.html" target="_blank">详见用户协议</a>' +
    '</div>' +
    '<div class="actions">' +
    '<button id="pubCancel">取消</button>' +
    '<button class="primary" id="pubSubmit">发布</button>' +
    '</div>' +
    '</div>';

  document.body.appendChild(overlay);

  document.getElementById('pubCancel')!.addEventListener('click', function () {
    overlay.remove();
  });

  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) overlay.remove();
  });

  document.getElementById('pubSubmit')!.addEventListener('click', function () {
    const name = (document.getElementById('pubName') as HTMLInputElement).value.trim();
    if (!name) { alert('请填写模板名称'); return; }

    const data = {
      album_id: album.id,
      name: name,
      category: (document.getElementById('pubCategory') as HTMLSelectElement).value,
      description: (document.getElementById('pubDesc') as HTMLTextAreaElement).value.trim() || null,
      cover_url: album.thumbnail || (album as any).cover_url || null
    };

    const btn = document.getElementById('pubSubmit') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = '发布中...';

    fetch('/api/templates/publish', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(function (r) { return r.json(); }).then(function (result: any) {
      overlay.remove();
      if (result.error) {
        alert('发布失败：' + result.message);
        trackEvent('community_template_publish_failed', { album_id: album.id, message: result.message || '发布失败' }, { immediate: true });
      } else {
        showToast('已发布到商城');
        albumPublishStates[album.id] = result;
        _loadTemplateData().then(function (m) {
          m.markTemplateDataChanged({ scope: 'community', templateIds: [String(result.id)], at: Date.now() });
        });
        refreshWorksView();
        trackEvent('community_template_publish_success', { published_template_id: result.id, category: result.category }, { immediate: true });
      }
    }).catch(function () {
      btn.disabled = false;
      btn.textContent = '发布';
      alert('发布失败，请重试');
      trackEvent('community_template_publish_failed', { album_id: album.id, message: '网络错误' }, { immediate: true });
    });
  });
}

export function refreshWorksView(): void {
  renderView('works');
}

// ========================
// Credits
// ========================

export function showCreditsHistory(): void {
  const overlay = document.createElement('div');
  overlay.className = 'publish-overlay';
  overlay.innerHTML =
    '<div class="publish-dialog" style="width:500px;max-height:70vh;overflow-y:auto;">' +
    '<h3>积分明细</h3>' +
    '<div id="creditsHistoryContent" style="text-align:center;color:var(--text-dim);padding:20px;">加载中...</div>' +
    '<div class="actions" style="margin-top:12px;"><button id="creditsHistoryClose">关闭</button></div>' +
    '</div>';
  document.body.appendChild(overlay);

  document.getElementById('creditsHistoryClose')!.addEventListener('click', function () { overlay.remove(); });
  overlay.addEventListener('click', function (e) { if (e.target === overlay) overlay.remove(); });

  fetch('/api/credits/transactions?limit=50', {
    credentials: 'include'
  }).then(function (r) { return r.json(); }).then(function (data) {
    const txns = data.transactions || [];
    if (!txns.length) {
      document.getElementById('creditsHistoryContent')!.innerHTML = '<p>暂无积分记录</p>';
      return;
    }
    const sourceLabels: Record<string, string> = {
      'order_complete': '下单返积分',
      'template_used': '你的模板被 1 人使用',
      'payment_deduct': '支付抵扣'
    };
    let html = '<table style="width:100%;font-size:12px;border-collapse:collapse;">' +
      '<thead><tr style="color:var(--text-dim);border-bottom:1px solid var(--glass-border);">' +
      '<th style="padding:6px;text-align:center;">时间</th>' +
      '<th style="padding:6px;text-align:center;">来源</th>' +
      '<th style="padding:6px;text-align:center;">变动</th>' +
      '<th style="padding:6px;text-align:center;">余额</th></tr></thead><tbody>';
    txns.forEach(function (t: any) {
      const date = new Date(t.created_at).toLocaleDateString('zh-CN');
      const amountStr = t.amount > 0 ? '+' + t.amount : t.amount.toString();
      const color = t.amount > 0 ? 'color:#155724;' : 'color:#721c24;';
      html += '<tr style="border-bottom:1px solid var(--glass-border);">' +
        '<td style="padding:6px;text-align:center;">' + date + '</td>' +
        '<td style="padding:6px;font-size:11px;text-align:center;">' + (sourceLabels[t.source] || t.source) + '</td>' +
        '<td style="padding:6px;text-align:center;' + color + '">' + amountStr + '</td>' +
        '<td style="padding:6px;text-align:center;">' + t.balance_after + '</td></tr>';
    });
    html += '</tbody></table>';
    document.getElementById('creditsHistoryContent')!.innerHTML = html;
  }).catch(function () {
    document.getElementById('creditsHistoryContent')!.innerHTML = '<p>加载失败</p>';
  });
}
